// An alternate in an add-on (Spencer, 2026-10-17): an item that stands in for a base item while its add-on is on. When it is
// another catalog item (a drawer base for a door base) it starts from that item: the sizes and every other input both
// items share carry over, but a price typed for the other item (a Cabinotch price, unit $) does not: the drawer base has
// its own quote, so it starts blank and the item says so. The children the replaced item makes that the alternate does
// not (the door base's applied end panel) are listed for a decision: kept under the alternate as its own row, or left out
// on purpose (job_item.children_left_out). An alternate prices like any item, with the full margin.

import type { Resolution, Scalar } from './types.ts';

/** What the carry rule reads of a catalog input. */
export interface InputShape {
  key: string;
  unit?: string | null;
  data_type?: string | null;
}

/** A price typed on one item: a number in dollars. */
const typedPrice = (i: InputShape | undefined) => !!i && String(i.unit ?? '').trim() === '$';

/**
 * The values an alternate starts with, from the item it replaces. The same catalog item carries everything (a solid-birch
 * L-04A is L-04 with a change); another catalog item carries the keys both items have, except a typed price, which is the
 * other item's quote. `blank` lists the typed prices left behind.
 */
export function carryToAlternate(from: InputShape[] | null, to: InputShape[], values: Record<string, Scalar>, sameItem: boolean): { carried: Record<string, Scalar>; blank: string[] } {
  const carried: Record<string, Scalar> = {};
  const blank: string[] = [];
  for (const [k, v] of Object.entries(values ?? {})) {
    if (v === null || v === undefined || String(v).trim() === '') continue;
    const target = to.find((i) => i.key === k);
    if (!target) continue;
    if (!sameItem && (typedPrice(target) || typedPrice(from?.find((i) => i.key === k)))) {
      blank.push(k);
      continue;
    }
    carried[k] = v;
  }
  return { carried, blank };
}

/** A child the replaced item makes that its alternate does not, waiting for Keep or Leave out. */
export interface AlternateGap {
  add_on_id: string | null;
  alternate_id: string;
  alternate_number: string;
  replaced_id: string;
  replaced_number: string;
  child: {
    id: string;
    number: string;
    name: string;
    catalog_item_id: string | null;
    catalog_item_name: string | null;
    /** What it was built to: the values typed on it or handed down by its parent, kept as typed when it is kept. */
    inputs: Record<string, Scalar>;
  };
}

/**
 * The children each alternate's replaced item makes that the alternate does not, by catalog item and count: a door base's
 * two doors against a drawer base's three drawer fronts of the same door item are kept; its applied end panel is not.
 * `base` is the job priced without the add-on, `withIt` the same job with it on. A child left out on purpose
 * (children_left_out on the alternate) is not listed again.
 */
export function alternateGaps(base: Resolution, withIt: Resolution): AlternateGap[] {
  const out: AlternateGap[] = [];
  for (const alt of withIt.items) {
    if (!alt.replaces_item_id) continue;
    const replaced = base.items.find((i) => i.id === alt.replaces_item_id);
    if (!replaced) continue;
    const had = base.items.filter((i) => i.parent_item_id === replaced.id);
    const has = withIt.items.filter((i) => i.parent_item_id === alt.id);
    const leftOut = alt.children_left_out ?? [];
    const kinds = [...new Set(had.map((c) => c.catalog_item_id ?? `custom:${c.id}`))];
    for (const kind of kinds) {
      const wanted = had.filter((c) => (c.catalog_item_id ?? `custom:${c.id}`) === kind);
      const covered = has.filter((c) => c.catalog_item_id === kind).length + leftOut.filter((k) => k === kind).length;
      for (const c of wanted.slice(covered)) {
        const inputs: Record<string, Scalar> = {};
        for (const i of c.inputs) if ((i.source === 'input' || i.source === 'parent' || i.source === 'override') && i.value !== null && i.value !== '') inputs[i.key] = i.value;
        out.push({
          add_on_id: alt.add_on_id ?? null,
          alternate_id: alt.id,
          alternate_number: alt.number,
          replaced_id: replaced.id,
          replaced_number: replaced.number,
          child: { id: c.id, number: c.number, name: c.name || c.catalog_item_name || '', catalog_item_id: c.catalog_item_id, catalog_item_name: c.catalog_item_name, inputs },
        });
      }
    }
  }
  return out;
}
